// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Singleton app wrapper class mainly for state and event management.
 * @author James Reid
 */

// @ts-check

// @@imports-dependencies
import { Spritesheet, Assets } from "pixi.js"
import spritesheetData from "../../../../admin/assets/images/spritesheets/compressed/isometric-named.json"
// import spritesheetData from "../../../../admin/assets/images/spritesheets/flat-half.json"

// @@imports-package
import { SteppedContainer } from "../components/index.js"

// @@imports-module
import { defaultState } from "./state.js"

// @@imports-utils
import {
    alertRequestError,
    apiRequest,
    cloneTemplate,
    crossFadeElements,
    fadeElementIn,
    fadeElementOut,
    getElementByAccessor
} from "../utils/index.js"

// @@imports-types
import { appStateCb, HTTP_METHODS, State } from "../types/index.js"
import { EpdmCalculator, Stock } from "../../../package/index.js"
import { billOfMaterials, deckEditor } from "../layouts/index.js"

// @@body
// 
const PUBLIC_ID_REGEX = /^[A-Za-z]{0,4}((?<=[A-Za-z]{4})-[A-Za-z]{0,4})?$/

class App extends HTMLElement {
    state = defaultState
    static #instance = /** @type {App?} */ (null)

    /**
     * 
     * @param {object} obj 
     * @param {SteppedContainer} obj.container
     * @returns {App}
     */
    constructor({ container }) {
        super()

        // Store references to stepped container and state.
        this.container = container

        // Return existing singleton instance if set, otherwise set private
        // static property as new class instance.
        if (App.#instance) { return App.#instance }
        App.#instance = this
    }

    async connectedCallback() {
        // Append stepped container to this instance of the app wrapper.
        this.append(this.container)

        // Fetch static App config details from URL query parameters and
        // apply results as required.
        const queryParams = new URLSearchParams(window.location.search)
        Object.assign(this.state.deck, {
            type: queryParams.get("deckType") || "flat"
        })
        Object.assign(this.state.pageOptions, {
            token: queryParams.get("token"),
            role: queryParams.get("role") || "client",
            lifecycle: queryParams.get("lifecycle") || "new",
            priceList: queryParams.get("priceList"),
            clientType: queryParams.get("clientType")
        })

        // fetch spritesheet and stock, and set merchant in state 
        try { await Promise.all([this.#setStock(), this.#setSpritesheet()]) }
        catch {
            // render error to client 
        }

        // Set background color of App to match host page if set.
        const backgroundColor = queryParams.get("backgroundColor")
        if (backgroundColor && backgroundColor.match(/^[0-9a-fA-F]{6}$/)) {
            document.body.style.backgroundColor = `#${backgroundColor}`
        }

        // 
        Object.assign(this.state.pageOptions, {
            hideBasket: this.state.quote.merchant.hideBasket,
            hidePrices: this.state.quote.merchant.hidePrices,
            showSurcharges: this.state.quote.merchant.misc?.showSurcharges
        })

        // 
        const quoteId = queryParams.get("quoteId")
        if (quoteId) {
            const quote = await apiRequest(`quotes/${this.state.quote.merchant.uuid}/${quoteId}`)
            this.state.quote.uuid = quoteId

            const addToBasket = queryParams.get("addToBasket")
            if (addToBasket === "true") {
                const lineItems = /** @type {any} */ ([])
                for (const { uuid, quantity } of quote.lineItems) {
                    const product = this.state.quote.stock
                        ?.getProductByUuid(uuid)

                    if (!product || product.stock < quantity) {
                        this.state.pageOptions.lifecycle = "failed"
                        return this.container.end()
                    }

                    lineItems.push({
                        uuid,
                        sku: product?.sku,
                        quantity,
                        price: (product?.price || 0) * quantity
                    })
                }

                const path = `quotes/${this.state.quote.merchant.uuid}/${quoteId}`
                apiRequest(path, {
                    method: HTTP_METHODS.patch,
                    body: { purchaseStatus: "basket" },
                    token: this.state.pageOptions.token
                })

                const message = {
                    action: "ADD_TO_BASKET",
                    uuid: quote.uuid,
                    quoteId: quote.quoteId,
                    lineItems
                }
                window.parent.postMessage(message, "*")

                this.state.pageOptions.lifecycle = "basket"
                return this.container.end()
            }

            if (quote.purchaseStatus !== "saved") {
                this.state.pageOptions.lifecycle = "no-modify"
                return this.container.end()
            }

            this.state.pageOptions.lifecycle = "updating"
            this.state.quote.calculator = EpdmCalculator
                .fromNormalized(quote.state, this.state.quote.stock)
            this.state.deck.client = this.state.quote.calculator.deck

            // Force hide basket for quote modification as this occurs only
            // on app subdomain and not on merchant iframe. Infer hide
            // prices from value in quote data.
            this.state.pageOptions.hideBasket = true
            this.state.pageOptions.hidePrices = quote.hidePrices
        }
        if (this.state.pageOptions.role !== "client") {
            this.state.pageOptions.hideBasket = true
        }

        // 
        const {
            hideBasket,
            hidePrices,
            showSurcharges
        } = this.state.pageOptions
        const showSales = this.state.pageOptions.role === "sales"
        if (hideBasket) { document.body.classList.add("--hide-basket") }
        if (hidePrices) { document.body.classList.add("--hide-prices") }
        if (showSurcharges) { document.body.classList.add("--show-surcharges") }
        if (showSales) { document.body.classList.add("--show-sales") }

        // Start stepped container calculator interface.
        // this.container.start()

        if (this.state.pageOptions.lifecycle === "updating") {
            const dummy = SteppedContainer.createContainer("content")
            await this.container.load(dummy, { unloadPopovers: false })
            this.container.renderStep(billOfMaterials)
        }
        else { this.container.start() }
    }

    async #setMerchant() {
        // Get query search param objects for 
        const queryParams = new URLSearchParams(window.location.search)

        // 
        const merchantId = queryParams.get("merchantId")
        if (merchantId) {
            // Test ID in search params against a public ID regex to identify
            // if string is a public ID or direct UUID.
            const path = PUBLIC_ID_REGEX.test(merchantId)
                ? `merchants?publicId=${merchantId}`
                : `merchants/${merchantId}`

            // Attempt to fetch merchant from ID passed in search params.
            const merchants = await apiRequest(path)
            const merchant = Array.isArray(merchants) ? merchants[0] : merchants
            if (merchant) {
                this.state.quote.merchant = merchant
                return merchant
            }
        }

        // 
        const merchants = []
        const merchantIds = queryParams.get("merchantIds")
            ?.replace(/\s/g, "")
            .split(/,/)
        if (merchantIds) {
            // 
            for (const merchantId of merchantIds) {
                const path = `merchants?publicId=${merchantId}`
                const merchant = (await apiRequest(path))[0]
                if (merchant) { merchants.push(merchant) }
            }
        }

        // 
        const modal = merchants.length
            ? this.#getMerchantOptionsModal(merchants)
            : await this.#getMerchantSearchModal()
        if (!modal) { return }
        this.container.loadModal(modal)

        return await new Promise(resolve => {
            // getElementByAccessor("", {
            //     parent: this,
            //     prototype: HTMLButtonElement.prototype
            // }).addEventListener("click", () => {
            //     this.container.unloadModal()
            //     resolve(this.state.quote.merchant)
            // })
            modal.querySelector("button.select")
                ?.addEventListener("click", () => {
                    this.container.unloadModal()
                    resolve(this.state.quote.merchant)
                })
        })
    }

    #getMerchantOptionsModal(merchants) {
        // Fetch required containers for merchant options modal.
        const modal = SteppedContainer.createContainer("modal")
        const merchantOptions = cloneTemplate("merchant-options")

        // Update card container with new element containing all merchant
        // options passed.
        merchantOptions.querySelector("div.card-grid")
            ?.replaceWith(this.#getMerchantCardContainer(merchants))

        // Append content to modal.
        modal.append(merchantOptions)

        return modal
    }

    async #getMerchantSearchModal() {
        // Ping API at /calculator root to dry-run merchant search requests
        // based on server environment. If server is in production environment,
        // ping will be rejected, triggering an error modal to show if the
        // merchantId query parameter is not set or if it does not correctly
        // resolve to a merchant.
        try { await apiRequest("", { reload: false }) }
        catch { return window.location.reload() }

        // Fetch required containers for  merchant select modal. 
        const modal = SteppedContainer.createContainer("modal")
        const merchantSearch = cloneTemplate("merchant-search")

        // Append content to modal.
        modal.append(merchantSearch)

        // 
        let cardContainer = /** @type {HTMLDivElement} */ (merchantSearch
            .querySelector("div.card-grid"))
        const notFound = /** @type {HTMLParagraphElement} */ (merchantSearch
            .querySelector("p#not-found"))
        const loadingContainer = /** @type {HTMLDivElement} */ (merchantSearch
            .querySelector("div.loading-container"))
        const buttonContainer = /** @type {HTMLDivElement} */ (merchantSearch
            .querySelector("div.button-container"))
        const searchButton = /** @type {HTMLButtonElement} */ (merchantSearch
            .querySelector("button.search"))
        const selectButton = /** @type {HTMLButtonElement} */ (merchantSearch
            .querySelector("button.select"))
        const nameInput = /** @type {HTMLInputElement} */ (merchantSearch
            .querySelector("input#branch-name"))
        // const idInput = /** @type {HTMLInputElement} */ (merchantSearch
        //     .querySelector("input#branch-id"))

        // 
        // idInput.addEventListener("input", () => {
        //     idInput.value
        //         .match(/^[A-Za-z]{0,4}((?<=[A-Za-z]{4})-[A-Za-z]{0,4})?$/)
        //             ? searchButton.classList.remove("inactive")
        //             : searchButton.classList.add("inactive")
        // })

        // 
        searchButton.addEventListener("click", async () => {
            // Ensure only animated loading bar. All other content should be
            // faded out.
            fadeElementOut(cardContainer)
            fadeElementOut(notFound)
            await crossFadeElements(buttonContainer, loadingContainer)

            // Get branch name and ID from input fields.
            const name = nameInput.value
            // const publicId = idInput.value.toLocaleUpperCase()

            // 
            // const params = new URLSearchParams({ name, publicId })
            const params = new URLSearchParams({ name })
            const path = `merchants?${params.toString()}`
            const merchants = await apiRequest(path, { allowedCodes: [404] })

            // 
            if (merchants.length) {
                cardContainer
                    .replaceWith(this.#getMerchantCardContainer(merchants))
                cardContainer = /** @type {HTMLDivElement} */
                    (merchantSearch.querySelector("div.card-grid"))


                searchButton.classList.add("secondary")
                searchButton.classList.remove("primary")
                selectButton.classList.remove("closed")

                fadeElementIn(cardContainer)
                crossFadeElements(loadingContainer, buttonContainer)
            }
            else {
                selectButton.classList.add("closed")
                searchButton.classList.add("primary")
                searchButton.classList.remove("secondary")

                fadeElementIn(notFound)
                crossFadeElements(loadingContainer, buttonContainer)
            }
        })

        return modal
    }

    #getMerchantCardContainer(merchants) {
        // 
        this.state.quote.merchant = merchants[0]

        // Create card container wrapper for all merchant options.
        const cardContainer = document.createElement("div")
        cardContainer.classList.add("card-grid")
        cardContainer.style.overflow = "scroll"

        // Create merchant card option for each merchant data object passed.
        for (const merchant of merchants) {
            // Clone merchant card option template and apply correct classes and
            // data values from merchant.
            const merchantOption = cloneTemplate("merchant-option")
            merchantOption.dataset.merchant = merchant.uuid
            if (merchant === this.state.quote.merchant) {
                merchantOption.classList.add("selected")
            }

            // Populate basic merchant details.
            /** @type {HTMLHeadingElement} */ (merchantOption
                .querySelector("span.merchant-name"))
                .innerText = merchant.name;
            /** @type {HTMLParagraphElement} */ (merchantOption
                .querySelector("span.merchant-phone"))
                .innerText = merchant.phone.replace(/(?<=\d{5})/, " ");
            /** @type {HTMLParagraphElement} */ (merchantOption
                .querySelector("span.merchant-email"))
                .innerText = merchant.email

            // Add selectable behaviour to each card, updating state when each
            // card is selected.
            merchantOption.addEventListener("click", () => {
                this.state.quote.merchant = merchant
                merchantOption.classList.add("selected")

                cardContainer.querySelectorAll("div.card")
                    .forEach((/** @type {any} */ card) => {
                        if (card.dataset.merchant !== merchant.uuid) {
                            card.classList.remove("selected")
                        }
                    })
            })

            // Append card to container.
            cardContainer.append(merchantOption)
        }

        return cardContainer
    }

    async #setStock() {
        // 
        const merchant = await this.#setMerchant()

        // 
        const params = new URLSearchParams()
        if (this.state.pageOptions.priceList) {
            params.set("priceList", this.state.pageOptions.priceList)
        }
        if (this.state.pageOptions.clientType) {
            params.set("clientType", this.state.pageOptions.clientType)
        }
        const path = `stock/${merchant.uuid}?${params.toString()}`
            .replace(/\?$/, "")

        // 
        const stock = await apiRequest(path)
        this.state.quote.stock = new Stock()
        for (const product of stock) {
            this.state.quote.stock.addProduct(product)
        }
        window.parent.postMessage({ action: "STOCK_LOADED", stock }, "*")

        return this.state.quote.stock
    }

    async #setSpritesheet() {
        const imageUrl = new URL(
            "../../../../admin/assets/images/spritesheets/compressed/isometric-named.jpg",
            // "../../../../admin/assets/images/spritesheets/flat-half.jpg",
            import.meta.url
        )
        spritesheetData.meta.image = imageUrl.href
        const texture = await Assets.load(spritesheetData.meta.image)

        return new Promise((resolve, reject) => {
            new Spritesheet(texture, spritesheetData)
                .parse()
                .then(spritesheet => {
                    this.state.spritesheet = spritesheet
                    resolve(this.state.spritesheet)
                })
                .catch(error => reject(error))
        })
    }

    /**
     * 
     * @param {appStateCb} callback 
     */
    static addStateListener(callback) {
        App.instance.addEventListener("roof-calc:state-updated", callback)
    }

    static removeStateListener(callback) {
        App.instance.removeEventListener("roof-calc:state-updated", callback)
    }

    /**
     * 
     */
    static dispatchStateUpdated() {
        App.instance.dispatchEvent(new CustomEvent("roof-calc:state-updated"))
    }

    /**
     * Get global instance of App wrapper element. Throws error if called before
     * app is initialized.
     */
    static get instance() {
        // Return initialized global instance of App wrapper if available.
        if (App.#instance instanceof App) { return App.#instance }

        // Throw error if App wrapper not yet initialized.
        throw new Error("Cannot access App wrapper before initialization")
    }

    /**
     * Get stepped container referenced by global instance of App wrapper.
     */
    static get container() {
        return App.instance.container
    }

    /**
     * Get state referenced by global instance of App wrapper.
     */
    static get state() {
        return App.instance.state
    }
}

// @@exports
export { App }
